/**
 * Schema-driven registry and category-based selection for the future Admin Agent (see docs/agentic-admin-assistant-plan.md).
 * Tool definitions use JSON Schema for parameters; the selection layer returns a bounded subset per request.
 */
const emptyObjectSchema = {
    type: 'object',
    properties: {},
    additionalProperties: false,
};
/**
 * Canonical registry (metadata + JSON Schema only). Execution lives in the future AdminAgent Lambda.
 */
export const ADMIN_TOOL_DEFINITIONS = [
    {
        id: 'answer_question',
        name: 'answer_question',
        description: 'Answer a general question about how to use the admin app or explain a concept without changing data.',
        parametersSchema: {
            type: 'object',
            properties: {
                question: { type: 'string', description: 'The user question to answer.' },
            },
            required: ['question'],
            additionalProperties: false,
        },
        category: 'global',
        keywords: ['help', 'how', 'what'],
    },
    {
        id: 'list_admin_capabilities',
        name: 'list_admin_capabilities',
        description: 'Summarize which high-level admin actions are available in the current context (no mutations).',
        parametersSchema: emptyObjectSchema,
        category: 'global',
    },
    {
        id: 'navigate_admin_screen',
        name: 'navigate_admin_screen',
        description: '**Client-side navigation** (React Router). The request **context** includes **navRoutes**: `{ path, label, section? }` from the live sidebar (NavStore) for this user and org — **use those paths first**. For `/app/course-editor/<id>` etc., `<id>` must be the real entity id from data, not a list index. Never `/app/course/...`.',
        parametersSchema: {
            type: 'object',
            properties: {
                path: {
                    type: 'string',
                    description: 'In-app path starting with `/`. For a course editor use `/app/course-editor/<id>` with the real course id from data, not a list index like 41.',
                },
                reason: {
                    type: 'string',
                    description: 'Short note for logs/UI (why we are navigating).',
                },
            },
            required: ['path'],
            additionalProperties: false,
        },
        category: 'global',
        keywords: [
            'go to',
            'open',
            'navigate',
            'switch page',
            'show screen',
            'AI configuration',
            'aiconfig',
        ],
    },
    {
        id: 'list_supported_admin_data_actions',
        name: 'list_supported_admin_data_actions',
        description: 'See which kinds of **reads and updates** the admin back office can perform (same catalog the app uses). Read-only overview; use before loading or changing specific records.',
        parametersSchema: emptyObjectSchema,
        category: 'graphql',
        keywords: [
            'lookup',
            'list',
            'search',
            'what can I',
            'available',
            'reports',
            'data',
        ],
    },
    {
        id: 'describe_fields_for_record_kind',
        name: 'describe_fields_for_record_kind',
        description: 'For a **kind of business record** (e.g. course, product), show which **fields and inputs** exist so you can ask for the right details. Read-only.',
        parametersSchema: {
            type: 'object',
            properties: {
                recordKind: {
                    type: 'string',
                    description: 'Name of the record or form kind (e.g. Course, Product, or the labels from the overview step).',
                },
            },
            required: ['recordKind'],
            additionalProperties: false,
        },
        category: 'graphql',
        keywords: ['fields', 'columns', 'attributes', 'shape', 'structure'],
    },
    {
        id: 'run_admin_data_operation',
        name: 'run_admin_data_operation',
        description: '**Look up or change** organization data **as this user**—same permissions as in the admin UI. Prefer read-only lookups unless the user clearly wants an update or create.',
        parametersSchema: {
            type: 'object',
            properties: {
                dataRequest: {
                    type: 'string',
                    description: 'Full GraphQL document (must start with `query` or `mutation`), not a bare operation name. Example: `query Q($organizationID: ID!) { listCourses(organizationID: $organizationID, limit: 25) { items { id name } } }`',
                },
                query: {
                    type: 'string',
                    description: 'Alias for dataRequest if the model emits `query` instead.',
                },
                variables: {
                    type: 'object',
                    description: 'Optional inputs (filters, IDs, or form values) for that request.',
                },
            },
            required: [],
            additionalProperties: false,
        },
        category: 'graphql',
        keywords: ['fetch', 'get', 'update', 'create', 'delete', 'save', 'load', 'pull'],
    },
    {
        id: 'get_course_outline',
        name: 'get_course_outline',
        description: 'Read-only: describe the current course structure (sections/lessons) when a course is in context.',
        parametersSchema: {
            type: 'object',
            properties: {
                courseId: {
                    type: 'string',
                    description: 'Course id; defaults to context.courseId when omitted.',
                },
            },
            additionalProperties: false,
        },
        category: 'course',
        keywords: ['course', 'lesson', 'section', 'outline'],
    },
    {
        id: 'get_product_summary',
        name: 'get_product_summary',
        description: 'Read-only: summarize basic product fields for the product in context (no mutations).',
        parametersSchema: {
            type: 'object',
            properties: {
                productId: {
                    type: 'string',
                    description: 'Product id; defaults to context.productId when omitted.',
                },
            },
            additionalProperties: false,
        },
        category: 'product',
        keywords: ['product', 'sku', 'catalog'],
    },
    {
        id: 'feed_status_hint',
        name: 'feed_status_hint',
        description: 'Read-only: explain what product feed features refer to in the admin (no pipeline execution).',
        parametersSchema: emptyObjectSchema,
        category: 'feed',
        keywords: ['feed', 'catalog', 'xml'],
    },
];
const DEFAULT_MAX_TOOLS = 50;
function normalizeRoute(route) {
    return (route || '').toLowerCase();
}
/**
 * Resolve which categories apply from route and resource ids.
 * Always includes `global`.
 */
export function resolveCategoriesFromContext(context) {
    const set = new Set(['global', 'graphql']);
    const route = normalizeRoute(context.route);
    if (context.courseId || context.sectionId !== undefined || route.includes('course')) {
        set.add('course');
    }
    if (context.productId || route.includes('product')) {
        set.add('product');
    }
    if (route.includes('feed')) {
        set.add('feed');
    }
    if (route.includes('org') || route.includes('organization')) {
        set.add('org');
    }
    if (route.includes('report')) {
        set.add('reporting');
    }
    return Array.from(set);
}
/**
 * Lightweight message hints: include an extra category when the message clearly references that domain.
 */
export function resolveCategoriesFromMessage(message) {
    if (message == null || !String(message).trim()) {
        return [];
    }
    const lower = String(message).toLowerCase();
    const out = new Set();
    if (/\b(feed|catalog xml|merchant feed)\b/i.test(lower)) {
        out.add('feed');
    }
    if (/\b(course|lesson|quiz|section)\b/i.test(lower)) {
        out.add('course');
    }
    if (/\b(product|sku|variant)\b/i.test(lower)) {
        out.add('product');
    }
    if (/\b(organization|org settings|tenant)\b/i.test(lower)) {
        out.add('org');
    }
    if (/\b(report|analytics|export)\b/i.test(lower)) {
        out.add('reporting');
    }
    if (/\b(graphql|appsync|api schema|introspection)\b/i.test(lower)
        || /\b(what (can|could) (i|we)|available actions|list (all )?(reads|updates)|record (kind|type|fields))\b/i.test(lower)) {
        out.add('graphql');
    }
    return Array.from(out);
}
/**
 * Merge category lists and dedupe.
 */
export function mergeCategories(...lists) {
    const set = new Set();
    for (const list of lists) {
        for (const c of list) {
            set.add(c);
        }
    }
    return Array.from(set);
}
/**
 * Returns tool definitions to attach for this request: category filter + stable sort + cap.
 */
export function getToolsForRequest(context, message, definitions = ADMIN_TOOL_DEFINITIONS, options = {}) {
    const maxTools = options.maxTools ?? DEFAULT_MAX_TOOLS;
    const categories = mergeCategories(resolveCategoriesFromContext(context), resolveCategoriesFromMessage(message), options.extraCategories ?? []);
    const allow = new Set(categories);
    const filtered = definitions.filter((d) => allow.has(d.category));
    const categoryOrder = [
        'global',
        'graphql',
        'course',
        'product',
        'feed',
        'org',
        'reporting',
    ];
    const rank = (c) => {
        const i = categoryOrder.indexOf(c);
        return i === -1 ? 99 : i;
    };
    filtered.sort((a, b) => {
        const dr = rank(a.category) - rank(b.category);
        if (dr !== 0)
            return dr;
        return a.id.localeCompare(b.id);
    });
    return filtered.slice(0, maxTools);
}
//# sourceMappingURL=toolRegistry.js.map